// /////////////////////////////////////////////////////////////
// [ファイル] AssistantAgentDialog.tsx
// [概要] AI アシスタントの「エージェント設定」ダイアログ。カスタム定義 (ルール / スキル /
//        ワークフロー / 取組み課題) を種別ごとに一覧し、各定義を画面上で直接編集できる。
//        - 所属グループはチップのプルダウンで付け替え (本文に触れない assign)
//        - 編集モードでは名前・説明・本文・ステータス・共有先・グループをテキスト入力で更新
//        - 保存は楽観ロック付き (他の利用者や AI が先に更新していれば 409 → 最新を提示)
//        保存・削除は Node 関数 manageAgent 経由 (useAssistantAgents)。AI ツール経由の更新と併存する。
// /////////////////////////////////////////////////////////////
import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../constants/icons';
import LoadingView from './LoadingView';
import AssistantMailAutomationTab from './AssistantMailAutomationTab';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import { useMasterData } from '../providers/MasterDataProvider';
import { useStaffNames } from '../hooks/useStaffNames';
import { useNotification } from '../providers/NotificationProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import {
  AgentConflictError,
  type AgentBuckets,
  type AgentGroup,
  type AgentItem,
  type AgentScope,
  type IssueStatus,
  type SaveAgentInput,
} from '../hooks/useAssistantAgents';

// 本文の全文注入サイズの警告閾値 (バックエンドの INJECTION_WARN_CHARS と同値)。入力欄の目安に出す
const BODY_WARN_CHARS = 16000;

// 取組み課題 (issue) のステータス表示。done は落ち着いた見た目 (outlined) にする
const ISSUE_STATUS_CHIP: Record<string, { label: string; color: 'default' | 'info' | 'success' }> = {
  open: { label: '未着手', color: 'default' },
  in_progress: { label: '対応中', color: 'info' },
  done: { label: '完了', color: 'success' },
};

interface StaffOption {
  email: string;
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  loading: boolean; // 開くたびの再取得中はローディング表示する
  agents: AgentBuckets;
  groups: AgentGroup[];
  currentGroupId: string; // 現在の会話のグループ ('' = 共通)
  currentUserEmail: string;
  onSaveAgent: (input: SaveAgentInput) => Promise<void>;
  onAssignGroup: (id: string, groupId: string, expectedUpdatedAt?: number) => Promise<void>;
  onDeleteAgent: (id: string) => Promise<void>;
  onOpenSession?: (sessionId: string) => void; // 「自動処理」タブの処理履歴から会話を開く
}

interface EditForm {
  id: string;
  type: AgentItem['type'];
  scope: AgentScope;
  name: string;
  description: string;
  body: string;
  enabled: boolean;
  status: IssueStatus;
  members: StaffOption[]; // 共通の shared のみ (作成者・本人は自動で含まれる)
  groupId: string;
  expectedUpdatedAt: number; // 楽観ロック (編集開始時の updated_at)
}

/** 定義が「誰に見えるか」。グループ付きはグループの共有先、共通は定義自身の共有先 */
function audienceOf(item: AgentItem, groupId: string, groupById: Map<string, AgentGroup>): string[] {
  if (groupId) {
    const g = groupById.get(groupId);
    if (!g) return [];
    return g.scope === 'shared' ? g.shared_with : [g.user_id];
  }
  return item.scope === 'shared' ? item.shared_with ?? [] : item.user_id ? [item.user_id] : [];
}

function sameSet(a: string[], b: string[]): boolean {
  const sa = new Set(a);
  const sb = new Set(b);
  if (sa.size !== sb.size) return false;
  for (const x of sa) if (!sb.has(x)) return false;
  return true;
}

export default function AssistantAgentDialog({
  open,
  onClose,
  loading,
  agents,
  groups,
  currentGroupId,
  currentUserEmail,
  onSaveAgent,
  onAssignGroup,
  onDeleteAgent,
  onOpenSession,
}: Props) {
  const notify = useNotification();
  const confirm = useConfirm();
  const { staffList } = useMasterData();
  const { formatMembers, resolve } = useStaffNames();

  const groupById = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);
  const enabledGroups = useMemo(() => groups.filter((g) => g.enabled), [groups]);
  const currentGroupName = currentGroupId ? groupById.get(currentGroupId)?.name ?? '' : '';
  const staffOptions = useMemo<StaffOption[]>(
    () =>
      (staffList as { staff_id?: string; staff_name?: string }[])
        .map((s) => ({ email: (s.staff_id ?? '').trim(), name: (s.staff_name ?? '').trim() }))
        .filter((s) => s.email && s.email !== currentUserEmail)
        .sort((a, b) => a.name.localeCompare(b.name, 'ja')),
    [staffList, currentUserEmail]
  );

  // 編集中の定義 (1 件ずつ)
  const [form, setForm] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  // 表示タブ。definitions = ルール等の定義、automation = メール自動処理 (監視状態・ルール・処理履歴)
  const [tab, setTab] = useState<'definitions' | 'automation'>('definitions');
  // 楽観ロック違反時の最新内容 (編集フォームの上に提示する)
  const [conflict, setConflict] = useState<AgentItem | null>(null);
  // グループ付け替え中・削除中の定義 id (行にローディングを出す)
  const [busyId, setBusyId] = useState<string | null>(null);
  // グループ付け替えメニュー (チップのプルダウン)
  const [groupMenu, setGroupMenu] = useState<{ anchorEl: HTMLElement; item: AgentItem } | null>(null);

  // 編集権限 (バックエンドと同じ判定)。グループ付きは参照できる時点で編集可、共通は作成者または共有メンバー
  const canEdit = (a: AgentItem): boolean => {
    if (a.group_id) return groupById.has(a.group_id);
    if (a.scope === 'personal') return a.user_id === currentUserEmail;
    return a.user_id === currentUserEmail || (a.shared_with ?? []).includes(currentUserEmail);
  };

  const startEdit = (a: AgentItem) => {
    setConflict(null);
    setForm({
      id: a.id,
      type: a.type,
      scope: a.scope,
      name: a.name,
      description: a.description ?? '',
      body: a.body ?? '',
      enabled: a.enabled !== false,
      status: a.status ?? 'open',
      members: (a.shared_with ?? [])
        .filter((e) => e !== a.user_id && e !== currentUserEmail)
        .map((e) => ({ email: e, name: resolve(e) })),
      groupId: a.group_id ?? '',
      expectedUpdatedAt: a.updated_at ?? 0,
    });
  };

  const cancelEdit = () => {
    setForm(null);
    setConflict(null);
  };

  // 保存前に「見える人が変わる」場合は確認する (共有の副作用を明示する)
  const confirmAudienceChange = async (item: AgentItem, nextGroupId: string, nextMembers?: string[]) => {
    const before = audienceOf(item, item.group_id ?? '', groupById);
    let after: string[];
    if (nextGroupId) {
      after = audienceOf(item, nextGroupId, groupById);
    } else if (nextMembers) {
      after = [...new Set([item.user_id ?? currentUserEmail, currentUserEmail, ...nextMembers])];
    } else {
      after = audienceOf(item, '', groupById);
    }
    const others = (list: string[]) => list.filter((e) => e !== currentUserEmail);
    if (sameSet(others(before), others(after))) return true;
    const added = others(after).filter((e) => !before.includes(e));
    const removed = others(before).filter((e) => !after.includes(e));
    const lines: string[] = [];
    if (added.length > 0) lines.push(`新たに見える人: ${formatMembers(added)}`);
    if (removed.length > 0) lines.push(`見えなくなる人: ${formatMembers(removed)}`);
    return confirm({
      title: '共有範囲が変わります',
      message: `「${item.name}」の見える人が変わります。${lines.join(' / ')}。変更した相手には Chat で通知されます。続けますか？`,
      confirmText: '続ける',
    });
  };

  const handleSave = async (item: AgentItem) => {
    if (!form) return;
    const name = form.name.trim();
    if (!name || !form.body.trim()) {
      notify.warning('名前と本文を入力してください');
      return;
    }
    const members = form.scope === 'shared' && !form.groupId ? form.members.map((m) => m.email) : undefined;
    const ok = await confirmAudienceChange(
      item,
      form.groupId,
      form.scope === 'shared' && !form.groupId ? members : form.scope === 'personal' && !form.groupId ? [] : undefined
    );
    if (!ok) return;
    setSaving(true);
    try {
      await onSaveAgent({
        id: form.id,
        type: form.type,
        scope: form.scope,
        name,
        description: form.description.trim(),
        body: form.body,
        enabled: form.enabled,
        status: form.type === 'issue' ? form.status : undefined,
        sharedWith: members,
        groupId: form.groupId,
        expectedUpdatedAt: form.expectedUpdatedAt,
      });
      notify.success('定義を保存しました');
      setForm(null);
      setConflict(null);
    } catch (e) {
      if (e instanceof AgentConflictError) {
        setConflict(e.latest);
        notify.warning('他の利用者または AI が先に更新しました。最新の内容を確認してください');
      } else {
        notify.error(e instanceof Error ? e.message : '定義の保存に失敗しました');
      }
    } finally {
      setSaving(false);
    }
  };

  // 競合時: 最新の内容でフォームを置き換える
  const adoptLatest = () => {
    if (!conflict) return;
    startEdit(conflict);
  };
  // 競合時: 自分の入力を保ったまま、最新の updated_at で保存し直せるようにする
  const keepMine = () => {
    if (!conflict || !form) return;
    setForm({ ...form, expectedUpdatedAt: conflict.updated_at ?? 0 });
    setConflict(null);
  };

  const handleAssign = async (item: AgentItem, groupId: string) => {
    setGroupMenu(null);
    if ((item.group_id ?? '') === groupId) return;
    const ok = await confirmAudienceChange(item, groupId);
    if (!ok) return;
    setBusyId(item.id);
    try {
      await onAssignGroup(item.id, groupId, item.updated_at);
      notify.success(groupId ? `「${groupById.get(groupId)?.name ?? ''}」へ移動しました` : '共通へ移動しました');
    } catch (e) {
      if (e instanceof AgentConflictError) {
        notify.warning('他の利用者または AI が先に更新したため、一覧を最新化しました。もう一度お試しください');
      } else {
        notify.error(e instanceof Error ? e.message : 'グループの変更に失敗しました');
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (item: AgentItem) => {
    const audience = audienceOf(item, item.group_id ?? '', groupById).filter((e) => e !== currentUserEmail);
    const ok = await confirm({
      title: '定義の削除',
      message:
        `「${item.name}」を削除しますか？元に戻せません。` +
        (audience.length > 0 ? ` 共有相手 (${formatMembers(audience)}) からも消え、Chat で通知されます。` : ''),
      confirmText: '削除する',
      confirmIcon: <Icons.Delete />,
      destructive: true,
    });
    if (!ok) return;
    setBusyId(item.id);
    try {
      await onDeleteAgent(item.id);
      notify.success('定義を削除しました');
      if (form?.id === item.id) cancelEdit();
    } catch (e) {
      notify.error(e instanceof Error ? e.message : '定義の削除に失敗しました');
    } finally {
      setBusyId(null);
    }
  };

  const handleClose = () => {
    if (saving || busyId) return;
    cancelEdit();
    onClose();
  };

  // 未完了を上、完了を下に並べる (リマインドとしての可視性を優先)
  const sortedIssues = [...agents.issues].sort(
    (a, b) => (a.status === 'done' ? 1 : 0) - (b.status === 'done' ? 1 : 0)
  );

  // --- 1 定義の行 (アコーディオン) ---------------------------------------------
  const renderItem = (a: AgentItem) => {
    const editable = canEdit(a);
    const editing = form?.id === a.id;
    const busy = busyId === a.id;
    const groupName = a.group_id ? groupById.get(a.group_id)?.name ?? '不明なグループ' : '共通';
    const inContext = (a.group_id ?? '') === currentGroupId || !a.group_id;
    return (
      <Accordion
        key={a.id}
        disableGutters
        elevation={0}
        expanded={editing ? true : undefined}
        sx={{
          border: 1,
          borderColor: editing ? 'primary.main' : 'divider',
          borderRadius: 1,
          mb: 0.75,
          bgcolor: '#fff',
          '&:before': { display: 'none' },
          overflow: 'hidden',
          opacity: a.enabled === false ? 0.6 : 1,
        }}
      >
        <AccordionSummary
          expandIcon={<Icons.ExpandMore sx={{ fontSize: 18 }} />}
          sx={{
            minHeight: 0,
            px: 1,
            // タイトル行は 1 行固定。長い名前・説明は省略表示し (全文は展開で見る)、
            // 行がダイアログ幅を左右に突き抜けないよう minWidth:0 で縮小を許可する
            '& .MuiAccordionSummary-content': {
              my: 0.75,
              alignItems: 'center',
              gap: 0.75,
              minWidth: 0,
              overflow: 'hidden',
            },
          }}
        >
          <Tooltip
            title={
              a.scope === 'shared' && (a.shared_with?.length ?? 0) > 0 && !a.group_id
                ? `共有メンバー: ${formatMembers(a.shared_with)}`
                : a.group_id
                  ? 'グループ付きの定義はグループの共有先に従います'
                  : ''
            }
          >
            <Chip
              size="small"
              icon={a.scope === 'shared' && !a.group_id ? <Icons.Groups /> : <Icons.Person />}
              label={a.scope === 'shared' && !a.group_id ? `共有 ${a.shared_with?.length ?? 0}人` : '個人'}
              sx={{ fontSize: '0.65rem', height: 20, flexShrink: 0 }}
            />
          </Tooltip>
          {a.status && ISSUE_STATUS_CHIP[a.status] && (
            <Chip
              size="small"
              variant={a.status === 'done' ? 'outlined' : 'filled'}
              color={ISSUE_STATUS_CHIP[a.status].color}
              label={ISSUE_STATUS_CHIP[a.status].label}
              sx={{ fontSize: '0.65rem', height: 20, flexShrink: 0 }}
            />
          )}
          {/* 所属グループ (プルダウン)。編集可能ならクリックでメニューを開いて付け替える。
              現在の会話に渡らない他グループの定義は淡色の outlined で区別する */}
          {busy ? (
            <Box sx={{ width: 90, flexShrink: 0 }}>
              <LoadingView message="" height="20px" size={16} />
            </Box>
          ) : (
            <Tooltip
              title={
                editable
                  ? 'クリックで所属グループを変更'
                  : inContext
                    ? 'この会話で AI に渡されます'
                    : '他グループの定義のため、現在の会話では AI に渡されません'
              }
            >
              <Chip
                size="small"
                variant={inContext ? 'filled' : 'outlined'}
                color={a.group_id && inContext ? 'primary' : 'default'}
                icon={a.group_id ? <Icons.Folder /> : <Icons.FolderOpen />}
                deleteIcon={<Icons.ArrowDropDown />}
                onDelete={editable ? (e) => setGroupMenu({ anchorEl: e.currentTarget, item: a }) : undefined}
                onClick={
                  editable
                    ? (e) => {
                        e.stopPropagation();
                        setGroupMenu({ anchorEl: e.currentTarget, item: a });
                      }
                    : undefined
                }
                label={groupName}
                sx={{ fontSize: '0.65rem', height: 20, flexShrink: 0, maxWidth: 160 }}
              />
            </Tooltip>
          )}
          <Typography variant="body2" noWrap sx={{ fontWeight: 600, flexShrink: 1, minWidth: '6em' }}>
            {a.name}
          </Typography>
          {a.description && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {a.description}
            </Typography>
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1, pt: 0, pb: 1, borderTop: 1, borderColor: 'divider' }}>
          {editing && form ? (
            saving ? (
              <LoadingView message="定義を保存しています..." height="160px" size={36} />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 1 }}>
                {conflict && (
                  <Alert
                    severity="warning"
                    sx={{ fontSize: '0.78rem' }}
                    action={
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" color="inherit" onClick={adoptLatest}>
                          最新の内容を表示
                        </Button>
                        <Button size="small" color="inherit" onClick={keepMine}>
                          自分の内容で上書き
                        </Button>
                      </Box>
                    }
                  >
                    他の利用者または AI が先にこの定義を更新しました
                    {conflict.updated_at ? ` (${new Date(conflict.updated_at).toLocaleString('ja-JP')})` : ''}。
                    最新の内容を確認してから保存し直すか、自分の内容で上書きしてください。
                  </Alert>
                )}
                <TextField
                  label="名前"
                  size="small"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  inputProps={{ maxLength: 60 }}
                />
                <TextField
                  label={
                    form.type === 'issue'
                      ? '課題の概要'
                      : form.type === 'rule'
                        ? '説明 (任意)'
                        : '用途の説明 (AI が該当する依頼か判断する材料)'
                  }
                  size="small"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  inputProps={{ maxLength: 300 }}
                />
                <TextField
                  label={
                    form.type === 'rule'
                      ? '本文 (常に従う指示)'
                      : form.type === 'skill'
                        ? '本文 (手順)'
                        : form.type === 'workflow'
                          ? '本文 (送信する定型プロンプト)'
                          : '本文 (背景・現状・進捗)'
                  }
                  size="small"
                  multiline
                  minRows={4}
                  maxRows={16}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value })}
                  helperText={`${form.body.length.toLocaleString()} 文字${
                    form.body.length > BODY_WARN_CHARS / 4
                      ? ` (全定義の合計が ${BODY_WARN_CHARS.toLocaleString()} 字を超えると注入が肥大化します)`
                      : ''
                  }`}
                />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id={`agent-group-${a.id}`}>所属グループ</InputLabel>
                    <Select
                      labelId={`agent-group-${a.id}`}
                      label="所属グループ"
                      value={form.groupId}
                      onChange={(e) => setForm({ ...form, groupId: String(e.target.value) })}
                      MenuProps={{ disableScrollLock: true }}
                    >
                      <MenuItem value="">共通 (全会話で渡す)</MenuItem>
                      {enabledGroups.map((g) => (
                        <MenuItem key={g.id} value={g.id}>
                          {g.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {form.type === 'issue' && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id={`agent-status-${a.id}`}>ステータス</InputLabel>
                      <Select
                        labelId={`agent-status-${a.id}`}
                        label="ステータス"
                        value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value as IssueStatus })}
                        MenuProps={{ disableScrollLock: true }}
                      >
                        <MenuItem value="open">未着手</MenuItem>
                        <MenuItem value="in_progress">対応中</MenuItem>
                        <MenuItem value="done">完了 (AI へ渡さない)</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={form.enabled}
                        onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                      />
                    }
                    label={<Typography variant="caption">有効 (AI へ渡す)</Typography>}
                  />
                </Box>
                {/* 共有設定は共通 (グループ無し) の定義だけ。グループ付きはグループの共有先に従う */}
                {!form.groupId ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        共有範囲
                      </Typography>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={form.scope}
                        onChange={(_, v) => {
                          if (v) setForm({ ...form, scope: v });
                        }}
                        // shared → personal は作成者のみ (他メンバーの手元から消えるため。バックエンドと同じ制約)
                        disabled={a.scope === 'shared' && a.user_id !== currentUserEmail}
                      >
                        <ToggleButton value="personal" sx={{ textTransform: 'none', px: 1.5 }}>
                          <Icons.Person sx={{ fontSize: 16, mr: 0.5 }} /> 個人
                        </ToggleButton>
                        <ToggleButton value="shared" sx={{ textTransform: 'none', px: 1.5 }}>
                          <Icons.Groups sx={{ fontSize: 16, mr: 0.5 }} /> 共有
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    {form.scope === 'shared' && (
                      <Autocomplete
                        multiple
                        size="small"
                        options={staffOptions}
                        value={form.members}
                        onChange={(_, v) => setForm({ ...form, members: v })}
                        getOptionLabel={(o) => (o.name ? `${o.name} (${o.email})` : o.email)}
                        isOptionEqualToValue={(x, y) => x.email === y.email}
                        renderInput={(params) => (
                          <TextField {...params} label="共有メンバー (作成者と自分は自動で含まれます)" />
                        )}
                      />
                    )}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    共有先はグループ「{groupById.get(form.groupId)?.name ?? ''}」の設定に従います
                    {(() => {
                      const g = groupById.get(form.groupId);
                      return g && g.scope === 'shared' ? ` (${formatMembers(g.shared_with)})` : ' (個人グループ)';
                    })()}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                  <Button size="small" onClick={cancelEdit} sx={STANDARD_TEXT_BUTTON_SX}>
                    キャンセル
                  </Button>
                  <Button
                    size="small"
                    onClick={() => void handleSave(a)}
                    startIcon={<Icons.Save />}
                    disabled={!form.name.trim() || !form.body.trim()}
                    sx={STANDARD_CTA_BUTTON_SX}
                  >
                    保存する
                  </Button>
                </Box>
              </Box>
            )
          ) : busy ? (
            <LoadingView message="更新しています..." height="80px" size={28} />
          ) : (
            <>
              {/* タイトル行で省略されても全文を確認できるよう、展開部の先頭に名前を出す */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                  {a.name}
                </Typography>
                {editable && (
                  <>
                    <Tooltip title="編集">
                      <IconButton size="small" onClick={() => startEdit(a)}>
                        <Icons.Edit sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="削除">
                      <IconButton size="small" onClick={() => void handleDelete(a)} sx={{ color: 'error.main' }}>
                        <Icons.Delete sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
              {a.description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, mt: 0.5 }}>
                  {a.description}
                </Typography>
              )}
              <Typography variant="caption" sx={{ display: 'block', whiteSpace: 'pre-wrap', color: 'text.primary' }}>
                {a.body}
              </Typography>
              {a.updated_at ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  最終更新 {new Date(a.updated_at).toLocaleString('ja-JP')}
                </Typography>
              ) : null}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderSection = (title: string, items: AgentItem[]) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: blueGrey[600], mb: 0.5 }}>
        {title}（{items.length}）
      </Typography>
      {items.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          まだ登録がありません
        </Typography>
      ) : (
        items.map(renderItem)
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          py: 0.75,
          px: 2,
          fontSize: '0.875rem',
          fontWeight: 600,
        }}
      >
        <Icons.Settings sx={{ color: blueGrey[400], fontSize: 18 }} />
        エージェント設定
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, v: 'definitions' | 'automation') => setTab(v)}
        sx={{ minHeight: 36, px: 2, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.8rem' } }}
      >
        <Tab value="definitions" label="定義 (ルール / スキル / ワークフロー / 課題)" />
        <Tab value="automation" icon={<Icons.Bolt sx={{ fontSize: 16 }} />} iconPosition="start" label="自動処理" />
      </Tabs>
      <DialogContent dividers sx={{ bgcolor: '#fafafa' }}>
        {tab === 'automation' ? (
          <AssistantMailAutomationTab
            active={open && tab === 'automation'}
            groups={enabledGroups}
            allGroups={groups}
            agents={agents}
            currentUserEmail={currentUserEmail}
            onOpenSession={(id) => {
              onOpenSession?.(id);
              handleClose();
            }}
          />
        ) : (
        <>
        <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
          ルール・スキル・ワークフロー・取組み課題は、チャットで AI に依頼して登録するほか、ここで直接編集できます
          （行を開いて鉛筆アイコン）。所属グループはチップのプルダウンで付け替えられます。
          未完了の取組み課題は AI が常に参照し、関連する話題で案内します。
          共有の変更は相手に Chat の DM で通知されます。
        </Alert>
        {loading ? (
          // 開くたびに最新定義を再取得するため、取得完了までは説明文の下にローディングを出す
          <LoadingView message="定義を読み込んでいます..." height="40vh" size={44} />
        ) : (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              現在の会話の文脈: {currentGroupName ? `共通 + グループ「${currentGroupName}」` : '共通のみ'}。
              グループ付きの定義は、そのグループを選んだ会話でだけ AI に渡されます。
            </Typography>
            {renderSection('ルール（常に従う指示）', agents.rules)}
            {renderSection('スキル（手順書）', agents.skills)}
            {renderSection('ワークフロー（定型プロンプト）', agents.workflows)}
            {renderSection('取組み課題（未完了は AI が会話のたびに参照）', sortedIssues)}
          </>
        )}
        </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 0.75 }}>
        <Button
          size="small"
          onClick={handleClose}
          disabled={saving || Boolean(busyId)}
          sx={{ ...STANDARD_TEXT_BUTTON_SX, fontSize: '0.78rem', py: 0.25 }}
        >
          閉じる
        </Button>
      </DialogActions>

      {/* 所属グループの付け替えメニュー (チップのプルダウン) */}
      <Menu
        anchorEl={groupMenu?.anchorEl ?? null}
        open={Boolean(groupMenu)}
        onClose={() => setGroupMenu(null)}
        disableScrollLock
      >
        <MenuItem
          dense
          selected={!groupMenu?.item.group_id}
          onClick={() => groupMenu && void handleAssign(groupMenu.item, '')}
        >
          <Icons.FolderOpen sx={{ fontSize: 16, mr: 1, color: blueGrey[400] }} />
          共通 (全会話で渡す)
        </MenuItem>
        {enabledGroups.map((g) => (
          <MenuItem
            key={g.id}
            dense
            selected={groupMenu?.item.group_id === g.id}
            onClick={() => groupMenu && void handleAssign(groupMenu.item, g.id)}
          >
            {g.scope === 'shared' ? (
              <Icons.Groups sx={{ fontSize: 16, mr: 1, color: blueGrey[400] }} />
            ) : (
              <Icons.Folder sx={{ fontSize: 16, mr: 1, color: blueGrey[400] }} />
            )}
            {g.name}
          </MenuItem>
        ))}
      </Menu>
    </Dialog>
  );
}
